import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './components/Login';
import { GoogleOAuthProvider } from '@react-oauth/google';

import FamilySelector from './components/FamilySelector';
import FamilyManagement from './components/FamilyManagement';
import RunbookList from './components/RunbookList';
import RunbookDetail from './components/RunbookDetail';
import CreateRunbook from './components/CreateRunbook';
import EditRunbook from './components/EditRunbook';
import SearchResults from './components/SearchResults';
import TemplateSelector from './components/TemplateSelector';
import AITemplateCreator from './components/AITemplateCreator';
import AdminDashboard from './components/AdminDashboard';
import InvitationAccept from './components/InvitationAccept';
import ConsentBanner from './components/ConsentBanner';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import CookiePolicyPage from './components/CookiePolicyPage';
import Footer from './components/Footer';
import DarkModeToggle from './components/DarkModeToggle';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { 
  Box, 
  Button, 
  Typography,
  Container,
  Paper,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import { ArrowBack, ContactPhone, RocketLaunch } from '@mui/icons-material';

import { apiCall } from './utils/api';
import { trackPageView, trackUserAction, initGA } from './utils/analytics';
import { isMobile, initializeMobileApp, hapticFeedback } from './utils/mobile';

function AppContent() {
  const { isDark } = useTheme();
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // App state
  const [currentView, setCurrentView] = useState('list');
  const [selectedRunbook, setSelectedRunbook] = useState(null);
  const [runbooks, setRunbooks] = useState([]);
  const [editingRunbook, setEditingRunbook] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [existingTemplate, setExistingTemplate] = useState(null);
  
  // Browser navigation state
  const [isNavigatingFromBrowser, setIsNavigatingFromBrowser] = useState(false);
  
  // Invitation state
  const [invitationToken, setInvitationToken] = useState(null);
  
  // Delete confirmation dialogs
  const [deleteRunbookDialogOpen, setDeleteRunbookDialogOpen] = useState(false);
  const [runbookToDelete, setRunbookToDelete] = useState(null);
  
  // Success/Error messages
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Navigate to a view and update browser URL
  const navigateToView = (view, params = {}) => {
    console.log('Navigating to view:', view, 'with params:', params);
    setCurrentView(view);
    
    // Update browser URL without triggering navigation
    const url = new URL(window.location);
    
    // Handle different view types
    if (view === 'list') {
      url.pathname = '/app';
    } else if (view === 'detail' && params.runbookId) {
      url.pathname = `/app/detail/runbook/${params.runbookId}`;
    } else if (view === 'search' && params.searchQuery) {
      url.pathname = '/app/search';
      url.searchParams.set('q', params.searchQuery);
    } else {
      url.pathname = `/app/${view}`;
    }
    
    // Add query parameters if provided
    Object.keys(params).forEach(key => {
      if (params[key] && key !== 'runbookId' && key !== 'searchQuery') {
        url.searchParams.set(key, params[key]);
      }
    });
    
    // Use replaceState to avoid adding to browser history
    window.history.replaceState({ view, params }, '', url.toString());
    console.log('Navigation complete. Current view:', view, 'URL:', url.toString());
  };

  // Handle browser back/forward navigation
  const handlePopState = useCallback((event) => {
    if (event.state && event.state.view) {
      setIsNavigatingFromBrowser(true);
      setCurrentView(event.state.view);
      
      // Restore any additional state from the navigation
      if (event.state.params) {
        const { params } = event.state;
        if (params.runbookId) {
          // Handle runbook detail view
          const runbook = runbooks.find(r => r.id === params.runbookId);
          if (runbook) {
            setSelectedRunbook(runbook);
          }
        }
        if (params.searchQuery) {
          setSearchQuery(params.searchQuery);
        }
      }
    }
  }, [runbooks]);

  // Handle title click to go back to home
  const handleTitleClick = () => {
    navigateToView('list');
  };



  const checkAuthStatus = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const data = await apiCall('/api/auth/me');
      console.log('Auth check data:', data);
      console.log('User role from auth check:', data.user?.role);
      setUser(data.user);
      setFamilies(data.families);
      setIsAuthenticated(true);

      // Auto-select family if user has only one
      if (data.families.length === 1) {
        setSelectedFamily(data.families[0]);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Token is invalid
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('families');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRunbooks = useCallback(async () => {
    if (!selectedFamily) return;

    try {
      console.log('Fetching runbooks for family:', selectedFamily.id);
      const data = await apiCall(`/api/families/${selectedFamily.id}/runbooks`);
      console.log('Fetched runbooks:', data);
      setRunbooks(data);
    } catch (error) {
      console.error('Error fetching runbooks:', error);
    }
  }, [selectedFamily]);

  // Runbook handlers
  const handleViewRunbook = (runbook) => {
    setSelectedRunbook(runbook);
    navigateToView('detail', { runbookId: runbook.id });
    hapticFeedback(); // Haptic feedback for runbook selection
  };

  const handleCreateRunbook = () => {
    trackUserAction('create_runbook_start');
    navigateToView('create');
    hapticFeedback(); // Haptic feedback for navigation
  };

  const handleCreateFromTemplate = () => {
    navigateToView('template-selector');
    hapticFeedback(); // Haptic feedback for navigation
  };

  const handleTemplateSelected = async (template, customizations) => {
    try {
      await apiCall(`/api/families/${selectedFamily.id}/runbooks/from-template/${template.id}`, {
        method: 'POST',
        body: JSON.stringify({ customizations })
      });
      
      // Refresh runbooks and go back to list
      await fetchRunbooks();
      navigateToView('list');
    } catch (error) {
      console.error('Error creating runbook from template:', error);
      alert('Failed to create runbook from template');
    }
  };

  const handleAdminDashboard = () => {
    navigateToView('admin');
  };

  const handleEditRunbook = (runbook) => {
    setEditingRunbook(runbook);
    navigateToView('edit', { runbookId: runbook.id });
  };

  const handleDeleteRunbook = (runbookId) => {
    const runbook = runbooks.find(r => r.id === runbookId);
    setRunbookToDelete(runbook);
    setDeleteRunbookDialogOpen(true);
  };

  const handleDeleteRunbookConfirm = async () => {
    try {
      const response = await apiCall(`/api/families/${selectedFamily.id}/runbooks/${runbookToDelete.id}`, {
        method: 'DELETE'
      });
      
      if (response.success !== false) {
        // Refresh the runbooks list
        await fetchRunbooks();
        // Show success message
        setSuccess('Runbook deleted successfully');
        setDeleteRunbookDialogOpen(false);
        setRunbookToDelete(null);
      } else {
        setError('Failed to delete runbook');
      }
    } catch (error) {
      console.error('Error deleting runbook:', error);
      setError(`Error deleting runbook: ${error.message}`);
    }
  };

  const handleDeleteRunbookCancel = () => {
    setDeleteRunbookDialogOpen(false);
    setRunbookToDelete(null);
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const response = await apiCall(`/api/templates/${templateId}`, {
        method: 'DELETE'
      });
      
      if (response.success !== false) {
        // Show success message
        alert('Template deleted successfully');
        return true; // Indicate success
      } else {
        alert('Failed to delete template');
        return false;
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Error deleting template: ${error.message}`);
      return false;
    }
  };

  const handleTemplateCreated = async (templateData) => {
    try {
      setSuccess('AI template created successfully!');
      // Navigate back to template selector to show the new template
      navigateToView('template-selector');
    } catch (error) {
      console.error('Error handling template creation:', error);
      setError('Failed to process template creation');
    }
  };

  // Authentication handlers
  const handleLogin = (loginData) => {
    console.log('Login data received:', loginData);
    console.log('User role:', loginData.user?.role);
    setUser(loginData.user);
    setFamilies(loginData.families);
    setIsAuthenticated(true);
    
    // Trigger haptic feedback for successful login
    hapticFeedback();
    
    // Track login event
    trackUserAction('login');

    // Auto-select family if user has only one
    if (loginData.families.length === 1) {
      setSelectedFamily(loginData.families[0]);
      // Navigate to runbook list after auto-selecting family
      navigateToView('list');
    } else {
      // If user has multiple families, go to family selector
      navigateToView('family-selector');
    }
  };

  const handleLogout = () => {
    trackUserAction('logout');
    hapticFeedback(); // Haptic feedback for logout
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('families');
    setIsAuthenticated(false);
    setUser(null);
    setFamilies([]);
    setSelectedFamily(null);
    setRunbooks([]);
    setCurrentView('list');
  };

  // Family handlers
  const handleSelectFamily = (family) => {
    setSelectedFamily(family);
    setCurrentView('list');
    setSearchResults(null);
    setSearchQuery('');
    hapticFeedback(); // Haptic feedback for family selection
  };

  const handleCreateFamily = (newFamily) => {
    const updatedFamilies = [...families, { ...newFamily, user_role: 'admin' }];
    setFamilies(updatedFamilies);
    localStorage.setItem('families', JSON.stringify(updatedFamilies));
    setSelectedFamily({ ...newFamily, user_role: 'admin' });
    
    // Navigate to the runbooks list after creating a family
    navigateToView('list');
    
    hapticFeedback(); // Haptic feedback for family creation
  };

  const handleFamilyDeleted = (deletedFamilyId) => {
    // Remove the deleted family from the families list
    const updatedFamilies = families.filter(f => f.id !== deletedFamilyId);
    setFamilies(updatedFamilies);
    localStorage.setItem('families', JSON.stringify(updatedFamilies));
    
    // Clear the selected family if it was the deleted one
    if (selectedFamily && selectedFamily.id === deletedFamilyId) {
      setSelectedFamily(null);
    }
    
    // Navigate to family selector
    navigateToView('family-selector');
  };

  const handleManageFamily = () => {
    navigateToView('family-management');
    hapticFeedback(); // Haptic feedback for navigation
  };

  const handleBackToFamilySelector = () => {
    setSelectedFamily(null);
    navigateToView('family-selector');
    hapticFeedback(); // Haptic feedback for navigation
  };

  // Runbook handlers
  const handleBackToList = () => {
    navigateToView('list');
    setSelectedRunbook(null);
    setEditingRunbook(null);
    setSearchResults(null);
    setSearchQuery('');
    fetchRunbooks(); // Refresh list
    hapticFeedback(); // Haptic feedback for navigation
  };

  const handleBackToDetail = () => {
    navigateToView('detail');
    setEditingRunbook(null);
    fetchRunbooks(); // Refresh data
    hapticFeedback(); // Haptic feedback for navigation
  };

  const handleSearchResults = (results, query) => {
    if (results === null) {
      // Clear search
      setSearchResults(null);
      setSearchQuery('');
      navigateToView('list');
    } else {
      // Show search results
      setSearchResults(results);
      setSearchQuery(query);
      navigateToView('search', { searchQuery: query });
    }
  };

  // Invitation handlers
  const handleInvitationAccepted = async () => {
    // Clear invitation state and redirect to home
    setInvitationToken(null);
    window.history.pushState({}, '', '/');
    
    // Refresh auth status to get updated family list
    await checkAuthStatus();
    
    if (isAuthenticated) {
      // User is already logged in, just refresh families
      navigateToView('list');
    } else {
      // User needs to log in
      navigateToView('login');
    }
  };

  const handleInvitationError = () => {
    // Clear invitation state and redirect to home
    setInvitationToken(null);
    window.history.pushState({}, '', '/');
    navigateToView('list');
  };

  // Legal page handlers
  const handleBackFromLegalPage = () => {
    window.history.pushState({}, '', '/');
    if (isAuthenticated) {
      navigateToView(selectedFamily ? 'list' : 'family-selector');
    } else {
      navigateToView('login');
    }
  };

  const handleViewExistingTemplate = async (templateTitle) => {
    try {
      // Fetch all templates and find the one with matching title
      const response = await apiCall('/api/templates');
      
      if (response && response.length > 0) {
        // Find the exact match by title (case-insensitive)
        const exactMatch = response.find(template => 
          template.title.toLowerCase() === templateTitle.toLowerCase()
        );
        
        if (exactMatch) {
          setExistingTemplate(exactMatch);
          navigateToView('view-template');
        } else {
          // If no exact match, show the first result
          setExistingTemplate(response[0]);
          navigateToView('view-template');
        }
      } else {
        alert('No templates found. The template may have been deleted.');
      }
    } catch (error) {
      console.error('Error finding existing template:', error);
      alert('Error finding existing template. Please try again.');
    }
  };

  const handleBackFromTemplate = () => {
    setExistingTemplate(null);
    navigateToView('template-selector');
  };

  // useEffect hooks - placed after all function definitions
  
  // Main initialization useEffect - runs only once on mount
  useEffect(() => {
    // Initialize mobile app features
    initializeMobileApp();
    
    // Initialize Google Analytics with consent defaults
    initGA();
    
    // Initialize Facebook SDK
    const initFacebookSDK = () => {
      if (window.FB) {
        console.log('Facebook SDK already initialized');
        return;
      }
      
      // Load Facebook SDK script
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      
      script.onload = () => {
        window.FB.init({
          appId: process.env.REACT_APP_FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
        console.log('Facebook SDK initialized successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load Facebook SDK');
      };
      
      document.head.appendChild(script);
    };
    
    initFacebookSDK();
    
    // Initialize mobile features
    if (isMobile()) {
      console.log('Running on mobile device');
      // Initialize mobile-specific features here
    }
    
    // Add browser navigation event listener
    window.addEventListener('popstate', handlePopState);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]); // Include handlePopState in dependencies

  // URL handling useEffect - runs when URL changes
  useEffect(() => {
    // Check for invitation token in URL
    const path = window.location.pathname;
    const inviteMatch = path.match(/^\/invite\/(.+)$/);
    
    if (inviteMatch) {
      setInvitationToken(inviteMatch[1]);
      setCurrentView('invitation');
      setIsLoading(false);
      return;
    }

    // Check for legal pages
    if (path === '/privacy-policy') {
      setCurrentView('privacy-policy');
      setIsLoading(false);
      return;
    }

    if (path === '/terms-of-service') {
      setCurrentView('terms-of-service');
      setIsLoading(false);
      return;
    }

    if (path === '/cookie-policy') {
      setCurrentView('cookie-policy');
      setIsLoading(false);
      return;
    }
    
    // Check if user is trying to access the app
    if (path.startsWith('/app') && !isAuthenticated) {
      // Show login form instead of redirecting
      setIsLoading(false);
      return;
    }
    
    // Handle app navigation from URL
    if (path.startsWith('/app/')) {
      const pathParts = path.split('/');
      const view = pathParts[2] || 'list';
      
      // Handle specific views with parameters
      if (view === 'detail' && path.includes('/runbook/')) {
        const runbookId = path.split('/runbook/')[1];
        if (runbookId) {
          setCurrentView('detail');
          // We'll set the runbook when it's loaded
        }
      } else if (view === 'search') {
        const searchQuery = new URLSearchParams(window.location.search).get('q');
        if (searchQuery) {
          setSearchQuery(searchQuery);
        }
        setCurrentView('search');
      } else {
        setCurrentView(view);
      }
    } else if (path === '/app') {
      setCurrentView('list');
    }
    
    checkAuthStatus();
  }, [isAuthenticated, checkAuthStatus]); // Only depend on these two

  useEffect(() => {
    if (selectedFamily) {
      fetchRunbooks();
    }
  }, [selectedFamily, fetchRunbooks]);

  // Track page views when currentView changes
  useEffect(() => {
    const getPageTitle = () => {
      switch (currentView) {
        case 'list': return 'Runbook List';
        case 'detail': return `Runbook: ${selectedRunbook?.title || 'Detail'}`;
        case 'create': return 'Create Runbook';
        case 'edit': return `Edit: ${editingRunbook?.title || 'Runbook'}`;
        case 'search': return `Search Results: ${searchQuery}`;
        case 'family-management': return 'Family Management';
        case 'template-selector': return 'Template Selector';
        case 'view-template': return `Template: ${existingTemplate?.title || 'View'}`;
        case 'admin': return 'Admin Dashboard';
        case 'invitation': return 'Accept Invitation';

        default: return isAuthenticated ? (selectedFamily ? 'Life Runbooks' : 'Select Family') : 'Login';
      }
    };

    const pagePath = `/${currentView}`;
    const pageTitle = getPageTitle();
    trackPageView(pagePath, pageTitle);
  }, [currentView, selectedRunbook, editingRunbook, searchQuery, isAuthenticated, selectedFamily, existingTemplate]);

  // Sync URL when currentView changes (but not from browser navigation)
  useEffect(() => {
    if (!isNavigatingFromBrowser && isAuthenticated) {
      // Update URL to match current view
      const url = new URL(window.location);
      
      if (currentView === 'list') {
        url.pathname = '/app';
      } else if (currentView === 'detail' && selectedRunbook) {
        url.pathname = `/app/detail/runbook/${selectedRunbook.id}`;
      } else if (currentView === 'search' && searchQuery) {
        url.pathname = '/app/search';
        url.searchParams.set('q', searchQuery);
      } else {
        url.pathname = `/app/${currentView}`;
      }
      
      // Remove any existing query params that are no longer relevant
      if (currentView !== 'search') {
        url.searchParams.delete('q');
      }
      
      window.history.replaceState({ view: currentView }, '', url.toString());
    }
    
    // Reset the flag
    setIsNavigatingFromBrowser(false);
  }, [currentView, selectedRunbook, searchQuery, isAuthenticated, isNavigatingFromBrowser]);

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }



  // Handle legal pages
  if (currentView === 'privacy-policy') {
    return (
      <div className="App">
        <PrivacyPolicyPage onBack={handleBackFromLegalPage} />
      </div>
    );
  }

  if (currentView === 'terms-of-service') {
    return (
      <div className="App">
        <TermsOfServicePage onBack={handleBackFromLegalPage} />
      </div>
    );
  }

  if (currentView === 'cookie-policy') {
    return (
      <div className="App">
        <CookiePolicyPage onBack={handleBackFromLegalPage} />
      </div>
    );
  }

  // Handle invitation view
  if (currentView === 'invitation' && invitationToken) {
    return (
      <div className="App">
        <InvitationAccept
          token={invitationToken}
          onAccepted={handleInvitationAccepted}
          onError={handleInvitationError}
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Check if user is trying to access the app
    const path = window.location.pathname;
    if (path.startsWith('/app')) {
      return (
        <div className="App">
          <Login onLogin={handleLogin} />
          <ConsentBanner />
        </div>
      );
    }
    // Redirect to landing page for other unauthenticated users
    window.location.href = '/landing.html';
    return null;
  }

  if (!selectedFamily) {
      return (
    <div className="App">
      <header className="App-header">
        <Box sx={{ 
          maxWidth: 1200, 
          mx: 'auto', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: 4 
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h3" 
              component="h1" 
              onClick={handleTitleClick}
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              Life Runbooks
            </Typography>
            <Typography variant="h6" sx={{ 
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: '600px',
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)'
            }}>
              Emergency procedures and step-by-step guides for life situations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <DarkModeToggle />
            {user?.role === 'admin' && (
              <Button
                variant="outlined"
                onClick={handleAdminDashboard}
                sx={{ 
                  textTransform: 'none',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                  color: isDark ? 'white' : 'text.primary',
                  '&:hover': {
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                  }
                }}
              >
                Admin Dashboard
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleManageFamily}
              sx={{ 
                textTransform: 'none',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                color: isDark ? 'white' : 'text.primary',
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              Manage Family
            </Button>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{ 
                textTransform: 'none',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                color: isDark ? 'white' : 'text.primary',
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </header>

        <main className="App-main">
          <FamilySelector
            families={families}
            user={user}
            onSelectFamily={handleSelectFamily}
            onCreateFamily={handleCreateFamily}
          />
        </main>
        <Footer />
        <ConsentBanner />
      </div>
    );
  }



  return (
    <div className="App">
      <header className="App-header">
        <Box sx={{ 
          maxWidth: 1200, 
          mx: 'auto', 
          display: 'flex', 
          justifyContent: 'space-between', 
          flexWrap: 'wrap', 
          gap: { xs: 2, sm: 4 },
          // Mobile responsive adjustments
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  onClick={handleTitleClick}
                  sx={{ 
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: { xs: 0.5, sm: 2 },
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      opacity: 0.8
                    },
                    // Mobile responsive text sizing
                    fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' }
                  }}
                >
                  Life Runbooks
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 400,
                  lineHeight: 1.4,
                  maxWidth: '600px',
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                  // Mobile responsive text sizing
                  fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem' },
                  display: { xs: 'none', sm: 'block' }
                }}>
                  {selectedFamily.name} - Emergency procedures and step-by-step guides
                </Typography>
              </Box>
              
              {/* Mobile: Compact button row */}
              <Box sx={{ 
                display: { xs: 'flex', sm: 'none' },
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <DarkModeToggle />
                {user?.role === 'admin' && (
                  <Button
                    variant="outlined"
                    onClick={handleAdminDashboard}
                    size="small"
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 1,
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                      color: isDark ? 'white' : 'text.primary',
                      '&:hover': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                      }
                    }}
                  >
                    Admin
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={handleManageFamily}
                  size="small"
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    color: isDark ? 'white' : 'text.primary',
                    '&:hover': {
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  Manage
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleBackToFamilySelector}
                  size="small"
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    color: isDark ? 'white' : 'text.primary',
                    '&:hover': {
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  Switch
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleLogout}
                  size="small"
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    color: isDark ? 'white' : 'text.primary',
                    '&:hover': {
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  Logout
                </Button>
              </Box>
            </Box>
          </Box>
          
          {/* Desktop: Full button layout */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' },
            gap: 2, 
            alignItems: 'center', 
            flexWrap: 'wrap'
          }}>
            <DarkModeToggle />
            {user?.role === 'admin' && (
              <Button
                variant="outlined"
                onClick={handleAdminDashboard}
                sx={{ 
                  textTransform: 'none',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                  color: isDark ? 'white' : 'text.primary',
                  '&:hover': {
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                  }
                }}
              >
                Admin Dashboard
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleManageFamily}
              sx={{ 
                textTransform: 'none',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                color: isDark ? 'white' : 'text.primary',
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              Manage Family
            </Button>
            <Button
              variant="outlined"
              onClick={handleBackToFamilySelector}
              sx={{ 
                textTransform: 'none',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                color: isDark ? 'white' : 'text.primary',
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              Switch Family
            </Button>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{ 
                textTransform: 'none',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                color: isDark ? 'white' : 'text.primary',
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </header>

      <main className="App-main">
        {/* Debug: Current view is {currentView} */}
        {currentView === 'list' && (
          <RunbookList
            runbooks={runbooks}
            onViewRunbook={handleViewRunbook}
            onCreateRunbook={handleCreateRunbook}
            onCreateFromTemplate={handleCreateFromTemplate}
            onSearchResults={handleSearchResults}
            familyId={selectedFamily.id}
            onDeleteRunbook={handleDeleteRunbook}
          />
        )}

        {currentView === 'search' && (
          <SearchResults
            results={searchResults}
            searchQuery={searchQuery}
            onViewRunbook={handleViewRunbook}
            onBackToList={handleBackToList}
          />
        )}

        {currentView === 'detail' && selectedRunbook && (
          <RunbookDetail
            runbook={selectedRunbook}
            onBack={handleBackToList}
            onEdit={handleEditRunbook}
            familyId={selectedFamily.id}
          />
        )}

        {currentView === 'create' && (
          <CreateRunbook
            onBack={handleBackToList}
            onRunbookCreated={fetchRunbooks}
            familyId={selectedFamily.id}
            onCreateWithAI={() => navigateToView('ai-creator')}
          />
        )}

        {currentView === 'edit' && editingRunbook && (
          <EditRunbook
            runbook={editingRunbook}
            onBack={handleBackToDetail}
            onRunbookUpdated={fetchRunbooks}
            familyId={selectedFamily.id}
          />
        )}

        {currentView === 'family-management' && (
          <FamilyManagement
            family={selectedFamily}
            user={user}
            onBack={handleBackToList}
            onFamilyDeleted={handleFamilyDeleted}
          />
        )}

        {currentView === 'template-selector' && (
          <TemplateSelector
            onSelectTemplate={handleTemplateSelected}
            onBack={handleBackToList}
            familyId={selectedFamily.id}
            onDeleteTemplate={handleDeleteTemplate}
            onViewExistingTemplate={handleViewExistingTemplate}
            onCreateRunbook={() => navigateToView('create')}
          />
        )}

        {currentView === 'ai-creator' && (
          <AITemplateCreator
            onBack={handleBackToList}
            familyId={selectedFamily.id}
            onTemplateCreated={handleTemplateCreated}
            onViewExistingTemplate={handleViewExistingTemplate}
          />
        )}

        {currentView === 'view-template' && existingTemplate && (
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={handleBackFromTemplate}
                    sx={{ textTransform: 'none' }}
                  >
                    Back to Templates
                  </Button>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    📋 {existingTemplate.title}
                  </Typography>
                </Box>

                {/* Template Content */}
                <Stack spacing={3}>
                  {/* Description */}
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                      Description
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {existingTemplate.description}
                    </Typography>
                  </Box>

                  {/* Category */}
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                      Category
                    </Typography>
                    <Chip label={existingTemplate.category} color="primary" variant="outlined" />
                  </Box>

                  {/* Difficulty Level */}
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                      Difficulty Level
                    </Typography>
                    <Chip 
                      label={existingTemplate.difficulty_level} 
                      color={existingTemplate.difficulty_level === 'Easy' ? 'success' : 
                             existingTemplate.difficulty_level === 'Medium' ? 'warning' : 'error'} 
                      variant="outlined" 
                    />
                  </Box>

                  {/* Estimated Time */}
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                      Estimated Time
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      ⏱️ {existingTemplate.estimated_time}
                    </Typography>
                  </Box>

                  {/* Steps */}
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      Steps
                    </Typography>
                    <List>
                      {existingTemplate.steps && existingTemplate.steps.map((step, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                              {index + 1}.
                            </Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              typeof step === 'string' ? (
                                <Typography variant="body2">{step}</Typography>
                              ) : typeof step === 'object' && step !== null ? (
                                <Box>
                                  {step.title && (
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {step.title}
                                    </Typography>
                                  )}
                                  {step.description && (
                                    <Typography variant="body2">
                                      {step.description}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2">{String(step)}</Typography>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Emergency Contacts */}
                  {existingTemplate.emergency_contacts && existingTemplate.emergency_contacts.length > 0 && (
                    <Box>
                      <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        Emergency Contacts
                      </Typography>
                      <List>
                        {existingTemplate.emergency_contacts.map((contact, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemIcon>
                              <ContactPhone color="primary" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={
                                typeof contact === 'string' ? contact : contact.name || contact.phone || JSON.stringify(contact)
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Actions */}
                  <Box sx={{ textAlign: 'center', pt: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => handleTemplateSelected(existingTemplate, {})}
                      startIcon={<RocketLaunch />}
                      sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
                    >
                      Use This Template
                    </Button>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          </Container>
        )}

        {currentView === 'admin' && user?.role === 'admin' && (
          <AdminDashboard
            onBack={handleBackToList}
          />
        )}

        {/* Delete Runbook Confirmation Dialog */}
        <Dialog
          open={deleteRunbookDialogOpen}
          onClose={handleDeleteRunbookCancel}
          aria-labelledby="delete-runbook-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="delete-runbook-dialog-title">
            Delete Runbook
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{runbookToDelete?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleDeleteRunbookCancel} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteRunbookConfirm} 
              variant="contained" 
              color="error"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Messages */}
        {success && (
          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
              {success}
            </Alert>
          </Snackbar>
        )}

        {error && (
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}
      </main>
      <Footer />
      <ConsentBanner />
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
